import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  CreditCard,
  Sparkles,
  MessageSquare,
  Package,
  Store,
  FolderKanban,
  Users,
  BarChart3,
  FileText,
  Building2,
  FileSpreadsheet,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Zap,
  Menu,
  ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const MainLayout = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const BLDR_URL = process.env.REACT_APP_BLDR_URL || 'https://bldr.companeze.com';
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: CreditCard, label: 'Pricing Plans', path: '/pricing-plans' },
    { icon: Sparkles, label: 'Advizzu', path: '/advizzu' },
    { icon: MessageSquare, label: 'AI Interview', path: '/ai-interview' },
    { icon: Package, label: 'Service Packages', path: '/service-packages' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: FolderKanban, label: 'Projects', path: '/projects' },
    { icon: Users, label: 'Client Portal', path: '/client-portal' },
    { icon: BarChart3, label: 'Analytics', path: '/analytics' },
    { icon: FileText, label: 'Advanced Reports', path: '/reports' },
    { icon: Building2, label: 'New Business', path: '/new-business' },
    { icon: FileSpreadsheet, label: 'Business Plans', path: '/business-plans' },
    { icon: DollarSign, label: 'Financials', path: '/financials' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('companeze_user');
    localStorage.removeItem('companeze_token');
    if (onLogout) onLogout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Menu Button */}
      <button
        className="fixed top-4 left-4 z-50 lg:hidden p-2 bg-white rounded-lg shadow-md"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Sidebar - Navy Blue */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-screen bg-blue-900 text-white flex flex-col z-40 transition-all duration-300",
          collapsed ? "w-20" : "w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo with Back Link */}
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          <a 
            href="/"
            className={cn("flex items-center gap-3 hover:opacity-80 transition-opacity", collapsed && "justify-center w-full")}
          >
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <span className="text-blue-900 font-bold text-sm">Cz</span>
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-bold text-lg leading-tight">Companeze</h1>
                <p className="text-[10px] text-amber-400 uppercase tracking-wider font-semibold">BLDR</p>
              </div>
            )}
          </a>
          <Button
            variant="ghost"
            size="icon"
            className={cn("text-blue-200 hover:text-white hover:bg-white/10 h-8 w-8", collapsed && "hidden")}
            onClick={() => setCollapsed(true)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
        </div>

        {/* Back to Main Site Link */}
        <div className="px-3 pt-3">
          <a
            href="/"
            className={cn(
              "flex items-center gap-2 px-3 py-2 text-blue-200 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm",
              collapsed && "justify-center"
            )}
          >
            <ChevronLeft className="w-4 h-4" />
            {!collapsed && <span>Back to Companeze</span>}
          </a>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    collapsed && "justify-center",
                    isActive
                      ? "bg-white/20 text-white"
                      : "text-blue-200 hover:text-white hover:bg-white/10"
                  )}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Go For Pro Button */}
        <div className="px-3 py-4 border-t border-white/10">
          <a
            href={BLDR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold rounded-lg transition-colors",
              collapsed && "px-2"
            )}
            data-testid="mainlayout-go-pro-btn"
          >
            {collapsed ? (
              <ExternalLink className="w-4 h-4" />
            ) : (
              <>
                <span>Go For Pro</span>
                <ExternalLink className="w-4 h-4" />
              </>
            )}
          </a>
        </div>

        {/* Collapse Toggle */}
        {collapsed && (
          <div className="px-3 py-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-full text-blue-200 hover:text-white hover:bg-white/10"
              onClick={() => setCollapsed(false)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Logout */}
        <div className="p-4 border-t border-white/10">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "text-blue-200 hover:text-white hover:bg-white/10 transition-colors w-full",
              collapsed ? "justify-center" : "justify-start"
            )}
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && <span className="ml-2">Sign Out</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main Content */}
      <main
        className={cn(
          "transition-all duration-300 min-h-screen",
          collapsed ? "lg:ml-20" : "lg:ml-64"
        )}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default MainLayout;
